import type { ClientResult } from "@/types";

export type RemoteFileClientDependencies = {
  defaultTimeoutMs: number;
  fetch?: typeof globalThis.fetch;
  setTimeout?: typeof globalThis.setTimeout;
  clearTimeout?: typeof globalThis.clearTimeout;
};

export type RemoteFileOptions = {
  signal?: AbortSignal;
  timeoutMs?: number;
};

export const createRemoteFileClient = (
  dependencies: RemoteFileClientDependencies
) => {
  const fetchImplementation = dependencies.fetch ?? globalThis.fetch;
  const setTimer = dependencies.setTimeout ?? globalThis.setTimeout;
  const clearTimer = dependencies.clearTimeout ?? globalThis.clearTimeout;

  const request = async (
    url: string,
    method: "GET" | "HEAD",
    options: RemoteFileOptions = {}
  ): Promise<ClientResult<Response>> => {
    if (options.signal?.aborted) return { ok: false, error: "cancelled" };

    const controller = new AbortController();
    let timedOut = false;
    const timeout = setTimer(() => {
      timedOut = true;
      controller.abort();
    }, options.timeoutMs ?? dependencies.defaultTimeoutMs);
    const cancel = () => controller.abort();
    options.signal?.addEventListener("abort", cancel, { once: true });

    try {
      const response = await fetchImplementation(url, {
        method,
        signal: controller.signal,
      });
      return { ok: true, value: response };
    } catch {
      if (timedOut) return { ok: false, error: "timeout" };
      if (options.signal?.aborted) return { ok: false, error: "cancelled" };
      return { ok: false, error: "networkUnavailable" };
    } finally {
      clearTimer(timeout);
      options.signal?.removeEventListener("abort", cancel);
    }
  };

  return {
    exists: async (
      url: string,
      options?: RemoteFileOptions
    ): Promise<ClientResult<boolean>> => {
      const result = await request(url, "HEAD", options);
      return "error" in result
        ? { ok: false, error: result.error }
        : { ok: true, value: result.value.ok };
    },
    download: async (
      url: string,
      options?: RemoteFileOptions
    ): Promise<ClientResult<Blob>> => {
      const result = await request(url, "GET", options);
      if ("error" in result) return { ok: false, error: result.error };
      if (!result.value.ok) return { ok: false, error: "invalidResponse" };
      try {
        return { ok: true, value: await result.value.blob() };
      } catch {
        return { ok: false, error: "invalidResponse" };
      }
    },
  };
};

export type RemoteFileClient = ReturnType<typeof createRemoteFileClient>;
